"use client";
import type React from "react";
import { useState, useEffect } from "react";
import { AlertCircle } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import toast from "react-hot-toast";
import axios from "axios";
import vLogo from "../components/images/vilva-new-logo.webp";
import Link from "next/link";
import { SigninRightSec } from "./SigninRightSec";
import Select from "react-select";
import { useUser } from "./userContext";
import { getFlagUrl } from "@/lib/imageUrl";
import { getApiUrl } from "@/lib/apiUrl";
import AuthMethodToggle, { AuthMethod } from "./AuthMethodToggle";

interface FormData {
  name: string;
  Mobile: string;
  email: string;
  password: string;
  country: string;
  honeypot: string;
  countryCode?: string;
}

interface FormErrors {
  Mobile?: string;
  email?: string;
  password?: string;
  country?: string;
}

interface Country {
  id: number;
  country: string;
  short_code: string;
  calling_code: number;
  phone_length: number;
  status: number;
  flag: string;
  currency: string;
  currency_symbol: string;
}

interface CustomOption {
  value: string;
  label: string;
  flag: string;
  calling_code: string;
  country_name: string;
  phone_length: number;
}

interface VisitorData {
  city: string;
  region: string;
  country: string;
  postal: string;
  latitude: string;
  longitude: string;
  org: string;
}

export default function SigninCard() {
  const { login, isLoggedIn, user, cartinfoData } = useUser();
  const router = useRouter();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    Mobile: "",
    email: "",
    country: "",
    password: "",
    honeypot: "",
    countryCode: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [authMethod, setAuthMethod] = useState<AuthMethod>("mobile");
  const [step, setStep] = useState<"account" | "otp" | "password">("account");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [key] = useState("waSs57FdgthqfzVkv3RMrpTjBcYGKJJRQWtNxhmbS2EPpUjwy8");
  const [countries, setCountries] = useState<Country[]>([]);
  const [isCountryLoading, setIsCountryLoading] = useState(true);
  const assetscountries = getFlagUrl();
  const [visitorData, setVisitorData] = useState<VisitorData>({
    city: "Unknown",
    region: "Unknown",
    country: "Unknown",
    postal: "Unknown",
    latitude: "Unknown",
    longitude: "Unknown",
    org: "Unknown",
  });

  // Fetch countries
  useEffect(() => {
    setIsCountryLoading(true);
    fetch("/api/countries")
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((data: Country[]) => {
        setCountries(Array.isArray(data) ? data : []);
        // Default to India immediately so the field never sits blank — the IP
        // lookup below only *upgrades* this if it resolves to a different country.
        const india = Array.isArray(data) ? data.find((c) => c.calling_code === 91) : undefined;
        if (india) {
          setFormData((prev) => ({
            ...prev,
            country: prev.country || india.id.toString(),
            countryCode: prev.countryCode || `${india.calling_code}`,
          }));
        }
        setIsCountryLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching countries:", error);
        notifyError("Failed to load country codes");
        setIsCountryLoading(false);
      });
  }, []);

  // Fetch visitor data and set default country
  useEffect(() => {
    if (countries.length > 0) {
      // Server-side lookup (no CORS/rate-limit exposure in the browser — see app/api/geo).
      fetch('/api/geo')
        .then((res) => res.json())
        .then((data) => {
          setVisitorData({
            city: data.city || "Unknown",
            region: data.region || "Unknown",
            country: data.country_code || "Unknown",
            postal: data.postal || "Unknown",
            latitude: data.latitude?.toString() || "Unknown",
            longitude: data.longitude?.toString() || "Unknown",
            org: data.org || "Unknown",
          });

          // Find the country matching the short_code from IP API
          const matchingCountry = countries.find((country) => country.short_code === data.country_code);
          if (matchingCountry) {
            setFormData((prev) => ({
              ...prev,
              country: matchingCountry.id.toString(),
              countryCode: `${matchingCountry.calling_code}`,
            }));
          } else {
            // Fallback to India (+91)
            const defaultCountry = countries.find((country) => country.calling_code === 91);
            if (defaultCountry) {
              setFormData((prev) => ({
                ...prev,
                country: defaultCountry.id.toString(),
                countryCode: `${defaultCountry.calling_code}`,
              }));
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching visitor data:", error);
          setVisitorData({
            city: "Unknown",
            region: "Unknown",
            country: "Unknown",
            postal: "Unknown",
            latitude: "Unknown",
            longitude: "Unknown",
            org: "Unknown",
          });
          // Fallback to India (+91) on error
          const defaultCountry = countries.find((country) => country.calling_code === 91);
          if (defaultCountry) {
            setFormData((prev) => ({
              ...prev,
              country: defaultCountry.id.toString(),
              countryCode: `${defaultCountry.calling_code}`,
            }));
          }
        });
    }
  }, [countries]);

  // Map countries to custom options for react-select
  const customOptions: CustomOption[] = countries.map((country) => ({
    value: country.id.toString(),
    label: `+${country.calling_code}`,
    flag: country.flag ? `${assetscountries}${country.flag}` : "",
    calling_code: `${country.calling_code}`,
    country_name: country.country,
    phone_length: country.phone_length,
  }));

  const formatOptionLabel = ({ calling_code, flag, country_name }: CustomOption) => (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      {flag && <img src={flag} alt={`${country_name} flag`} style={{ width: "20px" }} />}
      <span>{calling_code}</span>
    </div>
  );

  const validateEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const validateMobile = (mobile: string): boolean => {
    const selectedCountry = countries.find((country) => country.id.toString() === formData.country);
    const phoneLength = selectedCountry?.phone_length || 10;
    return new RegExp(`^\\d{${phoneLength}}$`).test(mobile.replace(/\s/g, ""));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (step === "account") {
      if (authMethod === "email") {
        if (!formData.email.trim()) newErrors.email = "Email is required";
        else if (!validateEmail(formData.email)) newErrors.email = "Enter a valid email address";
      } else {
        if (!formData.Mobile.trim()) newErrors.Mobile = "Mobile number is required";
        else if (!validateMobile(formData.Mobile)) {
          newErrors.Mobile = `Mobile number must be ${countries.find((c) => c.id.toString() === formData.country)?.phone_length || 10} digits`;
        }
        if (!formData.country) newErrors.country = "Country code is required";
      }
      if (!formData.password) newErrors.password = "Password is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleCountryChange = (selectedOption: CustomOption | null) => {
    if (selectedOption) {
      setFormData((prev) => ({
        ...prev,
        country: selectedOption.value,
        countryCode: selectedOption.calling_code,
        Mobile: /^\d+$/.test(prev.Mobile) ? "" : prev.Mobile,
      }));
      setErrors((prev) => ({ ...prev, country: undefined }));
    } else {
      setFormData((prev) => ({
        ...prev,
        country: "",
        countryCode: "",
      }));
    }
  };

  const countryCodeStyles = {
    control: (provided: any) => ({
      ...provided,
      borderRadius: "8px 0 0 8px",
      background: "white",
      fontFamily: "Latinka Regular",
      fontSize: "16px",
      color: "#090D28",
      minWidth: "85px",
      height: "42px",
      border: "1px solid #D1D7DD",
      borderRight: "none",
      outline: "none",
      display: "flex",
      alignItems: "center",
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "#090D28",
      fontFamily: "Latinka Regular",
      fontSize: "16px",
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      display: "none",
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      display: "none",
    }),
    menuList: (provided: any) => ({
      ...provided,
      maxHeight: "300px",
      "&::-webkit-scrollbar": {
        width: "6px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#2563eb",
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-track": {
        background: "#E6E9FF",
        borderRadius: "3px",
      },
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: "#090D28",
      fontFamily: "Latinka Regular",
      fontSize: "16px",
      display: "flex",
      alignItems: "center",
      height: "100%",
    }),
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setRememberMe(isChecked);
    localStorage.setItem("rememberMe", String(isChecked));
    if (!isChecked) {
      localStorage.removeItem("savedMobile");
      localStorage.removeItem("savedPassword");
    }
  };

  const encryptData = async (plainText: string, key: string) => {
    const encoder = new TextEncoder();
    try {
      const encodedKey = encoder.encode(key);
      const cryptoKey = await crypto.subtle.importKey(
        "raw",
        await crypto.subtle.digest("SHA-256", encodedKey),
        { name: "AES-GCM" },
        false,
        ["encrypt"]
      );
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const ciphertext = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv, tagLength: 128 },
        cryptoKey,
        encoder.encode(plainText)
      );
      const encryptedData = new Uint8Array([...iv, ...new Uint8Array(ciphertext)]);
      return btoa(String.fromCharCode(...encryptedData));
    } catch (err) {
      console.error("Encryption failed:", err);
      throw new Error("Encryption failed");
    }
  };

  const decryptData = async (encryptedData: string, key: string) => {
    const decoder = new TextDecoder();
    try {
      const data = Uint8Array.from(atob(encryptedData), (char) => char.charCodeAt(0));
      const iv = data.slice(0, 12);
      const ciphertext = data.slice(12);
      const encodedKey = new TextEncoder().encode(key);
      const cryptoKey = await crypto.subtle.importKey(
        "raw",
        await crypto.subtle.digest("SHA-256", encodedKey),
        { name: "AES-GCM" },
        false,
        ["decrypt"]
      );
      const decrypted = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv, tagLength: 128 },
        cryptoKey,
        ciphertext
      );
      return decoder.decode(decrypted);
    } catch (err) {
      console.error("Decryption failed:", err);
      throw new Error("Decryption failed");
    }
  };

  const notify = () =>
    toast.success("Welcome Back", {
      style: {
        border: "1px solid #00b852",
        padding: "16px",
        color: "#00b852",
        zIndex: "10000",
      },
      iconTheme: {
        primary: "#00b852",
        secondary: "#FFFAEE",
      },
    });

  const notifyError = (msg: string) =>
    toast.error(msg, {
      style: {
        border: "1px solid #cc0000",
        padding: "16px",
        color: "#cc0000",
        zIndex: "10000",
      },
      iconTheme: {
        primary: "#cc0000",
        secondary: "#FFFAEE",
      },
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.honeypot) return;
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const loginPayload =
        authMethod === "email"
          ? { email: formData.email.trim(), password: formData.password }
          : { mobile: `${formData.countryCode}${formData.Mobile}`, password: formData.password };
      const payload = {
        payload: await encryptData(JSON.stringify(loginPayload), key),
      };

      const result = await axios.post(
        getApiUrl("/customer/v1/login"),
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const decryptResData = await decryptData(result.data.payload, key);
      const parsedData = JSON.parse(decryptResData);
       console.log("parsedDataparsedData",parsedData)
      const { token, id, first_name, last_name, email, mobile } = parsedData.data;

      localStorage.setItem("authToken", parsedData.token);
      localStorage.setItem("userId", id);
      login({
        id: id || "",
        username: `${first_name || ""} ${last_name || ""}`.trim() || "Guest",
        email: email || "",
        mobile: mobile || "",
        token: parsedData.token || "",
        profile_pic: parsedData.data.profile_pic || "",
      });

      if (rememberMe) {
        localStorage.setItem("savedMobile", formData.Mobile);
        localStorage.setItem("savedPassword", formData.password);
      }

      notify();
      router.push("/portal");
    } catch (err: any) {
      console.error(err);
      if (err?.response?.data?.errors) {
        const errorObject = err.response.data.errors;
        Object.keys(errorObject).forEach((key) => {
          errorObject[key].forEach((errorMessage: string) => {
            notifyError(errorMessage);
          });
        });
      } else {
        notifyError(err?.response?.data?.message || "An unexpected error occurred");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const savedRememberMe = localStorage.getItem("rememberMe") === "true";
    if (savedRememberMe) {
      const savedMobile = localStorage.getItem("savedMobile") || "";
      setFormData((prev) => ({
        ...prev,
        Mobile: savedMobile,
        password: localStorage.getItem("savedPassword") || "",
      }));
    }
    setRememberMe(savedRememberMe);
  }, []);

  return (
    <div className="min-h-screen desktop-bg md:bg-[#DFF0FF]">
      <div className="flex justify-center bg-white py-[6px]">
        <Link href="/">
          <Image src={vLogo} alt="Vilva Business logo" className="w-[130px]" />
        </Link>
      </div>
      <div className="flex justify-center min-h-[85vh] items-start md:items-center md:px-4 lg:px-0">
        <div className="w-full mt-3 max-w-[918px]">
          <div className="grid my-1 mt-0 md:mt-3 md:grid-cols-2 gap-0 bg-white md:rounded-2xl md:shadow-[0px_292px_82px_rgba(6,58,140,0),0px_187px_75px_rgba(6,58,140,0.01),0px_105px_63px_rgba(6,58,140,0.05),0px_47px_47px_rgba(6,58,140,0.09),0px_12px_26px_rgba(6,58,140,0.10)] overflow-hidden">
            <div className="p-6 lg:p-[32px] md:!pb-[24px]">
              <div className="max-w-md mx-auto flex flex-col md:h-full h-[85vh] justify-between">
                <form onSubmit={handleSubmit} method="post" className="space-y-[18px]">
                  {step === "account" && (
                    <>
                      <div className="text-center mb-8">
                        <h1 className="text-[#002F81] text-center text-[24px] font-normal leading-[100%] font-NeueMontreal mb-2">
                          Welcome Back!
                        </h1>
                        <p className="text-[#6C6F87] text-[13px] font-normal leading-[140%] schibsted-grotesk">
                          Log in to take control of your business tools.
                        </p>
                      </div>
                      <input
                        type="text"
                        name="honeypot"
                        value={formData.honeypot}
                        onChange={(e) => handleInputChange("honeypot", e.target.value)}
                        className="hidden schibsted-grotesk"
                        tabIndex={-1}
                        autoComplete="off"
                      />
                      <AuthMethodToggle
                        method={authMethod}
                        onChange={(m) => {
                          setAuthMethod(m);
                          setErrors({});
                        }}
                      />
                      {authMethod === "mobile" && (
                      <div className="animate-in fade-in slide-in-from-top-1 duration-200">
                        <label
                          htmlFor="Mobile"
                          className="text-[#090D28] text-[14px] font-[500] leading-[100%] schibsted-grotesk mb-[5px]"
                        >
                          Mobile number <span style={{ color: "#EE3535" }}>*</span>
                        </label>
                        <div className="relative flex items-center">
                          <div className="relative ">
                            <Select
                              name="country"
                                instanceId="country-code-select"
                              options={customOptions}
                              value={customOptions.find((option) => option.value === formData.country) || null}
                              onChange={handleCountryChange}
                              formatOptionLabel={formatOptionLabel}
                              styles={countryCodeStyles}
                              placeholder="..."
                              isDisabled={isCountryLoading}
                              isLoading={isCountryLoading}
                              aria-label="Select country code"
                            />
                            <svg
                              className="absolute top-1/2 right-[6px] transform -translate-y-1/2"
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M4 6L8 10L12 6"
                                stroke="black"
                                strokeWidth="1.25"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <input
                            id="Mobile"
                            type="text"
                            aria-invalid={!!(errors.Mobile || errors.country)}
                            aria-describedby={(errors.Mobile || errors.country) ? "Mobile-error" : undefined}
                            value={formData.Mobile}
                            onChange={(e) => {
                              const maxLen = countries.find((c) => c.id.toString() === formData.country)?.phone_length || 10;
                              handleInputChange("Mobile", e.target.value.replace(/\D/g, '').slice(0, maxLen));
                            }}
                            placeholder="Enter mobile number"
                            className={`
                              w-full h-[42px] px-[16px] py-2.5 rounded-r-[8px] border border-[#D1D7DD] bg-white text-[#090D28]
                              schibsted-grotesk text-base font-normal leading-[120%]
                              outline-none transition-colors duration-200
                              placeholder:text-[#6C6F87] placeholder:schibsted-grotesk
                              placeholder:text-base placeholder:font-normal placeholder:leading-[100%]
                              focus:border-[#2563eb] focus:border focus:border-l-1 focus:ring-2 focus:ring-[#2563eb]/10
                              ${errors.Mobile || errors.country ? "border border-l-1 border-[#EE3535] focus:border-[#EE3535] focus:ring-[#EF4444]/10" : "border-l-0 focus:border-l-[#2563eb]"}
                            `}
                          />
                          {(errors.Mobile || errors.country) && (
                            <div className="text-[#EE3535] absolute top-3 right-3 text-[14px]">
                              <AlertCircle className="w-4 h-4 mr-1" />
                            </div>
                          )}
                        </div>
                        {(errors.Mobile || errors.country) && (
                          <div id="Mobile-error" role="alert" className="flex items-center text-[#EE3535] text-[14px] mt-[4px]">
                            {errors.Mobile || errors.country}
                          </div>
                        )}
                      </div>
                      )}
                      {authMethod === "email" && (
                      <div className="animate-in fade-in slide-in-from-top-1 duration-200">
                        <label
                          htmlFor="email"
                          className="text-[#090D28] text-[14px] font-[500] leading-[100%] schibsted-grotesk mb-[5px]"
                        >
                          Email <span style={{ color: "#EE3535" }}>*</span>
                        </label>
                        <div className="relative">
                          <input
                            id="email"
                            type="email"
                            aria-invalid={!!errors.email}
                            aria-describedby={errors.email ? "email-error" : undefined}
                            value={formData.email}
                            onChange={(e) => handleInputChange("email", e.target.value)}
                            placeholder="Enter your email"
                            className={`
                              w-full h-[42px] px-[16px] py-2.5 rounded-[8px] border border-[#D1D7DD] bg-white text-[#090D28]
                              schibsted-grotesk text-base font-normal leading-[120%]
                              outline-none transition-colors duration-200
                              placeholder:text-[#6C6F87] placeholder:schibsted-grotesk
                              placeholder:text-base placeholder:font-normal placeholder:leading-[100%]
                              focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/10
                              ${errors.email ? "border-[#EE3535] focus:border-[#EE3535] focus:ring-[#EF4444]/10" : ""}
                            `}
                          />
                          {errors.email && (
                            <div className="text-[#EE3535] absolute top-3 right-3 text-[14px]">
                              <AlertCircle className="w-4 h-4 mr-1" />
                            </div>
                          )}
                        </div>
                        {errors.email && (
                          <div id="email-error" role="alert" className="flex items-center text-[#EE3535] text-[14px] mt-[4px]">
                            {errors.email}
                          </div>
                        )}
                      </div>
                      )}
                      <div>
                        <label
                          htmlFor="password"
                          className="text-[#090D28] text-[14px] font-[500] leading-[100%] schibsted-grotesk mb-[5px]"
                        >
                          Password <span style={{ color: "#EE3535" }}>*</span>
                        </label>
                        <div className="relative">
                          <input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            aria-invalid={!!errors.password}
                            aria-describedby={errors.password ? "password-error" : undefined}
                            value={formData.password}
                            onChange={(e) => handleInputChange("password", e.target.value)}
                            placeholder="Enter password"
                            className={`
                              w-full h-[42px] px-4 py-2.5 rounded-[8px] border border-[#D1D7DD] bg-white text-[#090D28]
                              schibsted-grotesk text-base font-normal leading-[120%]
                              outline-none transition-colors duration-200
                              placeholder:text-[#6C6F87] placeholder:schibsted-grotesk
                              placeholder:text-base placeholder:font-normal placeholder:leading-[100%]
                              focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/10
                              ${errors.password ? "border-[#EE3535] focus:border-[#EE3535] focus:ring-[#EF4444]/10" : ""}
                            `}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute top-1/2 right-3 transform -translate-y-1/2"
                          >
                            {showPassword ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#6C6F87"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#6C6F87"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
                                <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
                                <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
                                <path d="m2 2 20 20" />
                              </svg>
                            )}
                          </button>
                          {errors.password && (
                            <div className="text-[#EE3535] absolute top-3 right-10 text-[14px]">
                              <AlertCircle className="w-4 h-4 mr-1" />
                            </div>
                          )}
                        </div>
                        {errors.password && (
                          <div id="password-error" role="alert" className="flex items-center text-[#EE3535] text-[14px] mt-[4px]">
                            {errors.password}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="rememberMe"
                            checked={rememberMe}
                            onChange={handleCheckboxChange}
                            className="mr-2 w-[22px] h-[22px] aspect-square rounded-[2px] !border-[0.8px] !border-[#D1D7DD] bg-white"
                          />
                          <label
                            htmlFor="rememberMe"
                            className="text-[#090D28] text-[16px] font-medium leading-[100%] schibsted-grotesk"
                          >
                            Remember me
                          </label>
                        </div>
                        <Link
                          className="text-[#195AFF] text-[16px] font-medium leading-[100%] schibsted-grotesk"
                          href={{
                            pathname: "/forgot-password",
                            query: {
                              mobile: formData.Mobile,
                              countryCode: formData.countryCode,
                            },
                          }}
                        >
                          Forgot Password?
                        </Link>
                      </div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full h-[42px] px-5 rounded-[22px] bg-[#195AFF] text-white schibsted-grotesk text-base font-medium leading-[120%] disabled:opacity-60 disabled:cursor-not-allowed  flex items-center justify-center"
                      >
                        {isSubmitting ? (
                          <div className="flex items-center">
                            <svg
                              className="animate-spin h-5 w-5 mr-2 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Logging in...
                          </div>
                        ) : (
                          "Log In"
                        )}
                      </button>
                      <p className="text-[#6C6F87] text-center text-[14px] font-normal leading-[100%] schibsted-grotesk">
                        Or
                      </p>
                      <Link
                        className="flex h-[42px] px-[20px] py-[16px] justify-center items-center self-stretch rounded-[22px] !border !border-[#195AFF] border-solid bg-white text-[#195AFF] text-[16px] font-[600] leading-[120%] schibsted-grotesk"
                        href={{
                          pathname: "/signin-with-otp",
                          query: {
                            mobile: formData.Mobile,
                            countryCode: formData.countryCode,
                          },
                        }}
                      >
                        Login with OTP
                      </Link>
                    </>
                  )}
                </form>
                <div>
                  <p className="text-[#6C6F87] m-0 mt-[16px] md:mt-[60px] text-[14px] text-center schibsted-grotesk">
                    By continuing, you agree to our{" "}
                    <Link
                      href="/terms-and-condition"
                      className="text-[#6C6F87] text-[13px] font-normal leading-[100%] underline schibsted-grotesk"
                    >
                      Terms
                    </Link>{" "}
                    &{" "}
                    <Link
                      href="/terms-and-condition"
                      className="text-[#6C6F87] text-[13px] font-normal leading-[100%] underline schibsted-grotesk"
                    >
                      Privacy Policy.
                    </Link>
                  </p>
                  <p className="text-[#686B7E] font-[600] schibsted-grotesk m-0 text-center text-[16px] mt-[16px] schibsted-grotesk">
                    Do you have no account?{" "}
                    <Link href="/signup" className="text-[#195AFF] schibsted-grotesk text-[16px]">
                      Signup Now
                    </Link>
                  </p>
                </div>
              </div>
            </div>
            <div className="hidden md:block h-full py-[30px] px-[48px] !pb-[24px] text-white login-bg">
              <SigninRightSec />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}